// frontend/src/styles/ThemeProvider.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from './theme';
import GlobalStyles from './GlobalStyles';

const THEME_STORAGE_KEY = 'gestion-taller-theme';

// ---------------------------------------------------------------------------
// Configuración de zoom automático por tamaño de pantalla
// ---------------------------------------------------------------------------
// Editar ZOOM_CONFIG para cambiar el zoom por rango de pantalla.
// Se usa viewport * devicePixelRatio para detectar mejor cuando la ventana
// cambia entre monitores con diferente escala (laptop + monitor externo).
//
// Rangos orientativos (ancho efectivo en px):
//   ≤ 1366px  →  13" a 15"   (laptops HiDPI, Surface, netbooks)
//   ≤ 1920px  →  15" a 20"   (monitores FHD, tablets 12" portrait a 1.5–2× DPR)
//   > 1920px  →  20" en adelante + tablets 12" a 2× DPR (iPad Pro, etc.)

export const ZOOM_CONFIG = [
  { maxWidth: 1366, zoom: 0.83 },  // 13" a 15" laptops
  { maxWidth: 1920, zoom: 1.0  },  // 15"-20" monitores + tablets 12" portrait
  { zoom: 1.0 },                   // 20" en adelante
];

const getEffectiveScreenWidth = () => {
  const viewportWidth = window.visualViewport?.width
    || window.innerWidth
    || document.documentElement.clientWidth
    || 0;
  const dpr = window.devicePixelRatio || 1;
  return Math.round(viewportWidth * dpr);
};

// Dispositivos táctiles (tablet/celular): sin mouse fino ni puntero preciso.
// matchMedia detecta esto de forma confiable independientemente del ancho/dpr.
const isTouchPrimaryDevice = () => {
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
};

export const getAutoZoom = () => {
  // El zoom automático compacta la UI en laptops de pantalla chica (uso con
  // mouse/teclado). En tablets Android se detectó que la propiedad CSS `zoom`
  // combinada con la unidad `dvh` deja huecos de altura sin usar (el navegador
  // no siempre recalcula bien el viewport dinámico bajo zoom) — confirmado en
  // una Samsung SM-T220, que cae en el tier de 0.83 por su dpr (~2.15) y sufre
  // el problema en ambas orientaciones, mientras que un celular con dpr más
  // alto (que cae en el tier 1.0, sin escalado) no lo sufre. En touch no hace
  // falta compactar nada: la tablet ya maneja su propio escalado de UI.
  if (isTouchPrimaryDevice()) return 1.0;

  const sw = getEffectiveScreenWidth();
  for (const tier of ZOOM_CONFIG) {
    if (tier.maxWidth === undefined || sw <= tier.maxWidth) return tier.zoom;
  }
  return 1.0;
};

// ---------------------------------------------------------------------------

// Contexto para el tema
const ThemeContext = createContext();

// Hook personalizado para usar el tema
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

// Componente ThemeProvider
const ThemeProvider = ({ children }) => {
  // Obtener tema inicial desde localStorage
  const getInitialTheme = () => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme === 'dark') return true;
      if (savedTheme === 'light') return false;
      // Sin preferencia guardada: usar la del sistema operativo
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (error) {
      return false;
    }
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);

  // Guardar tema en localStorage cuando cambie
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? 'dark' : 'light');
      // Agregar clase al body para compatibilidad
      document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
    } catch (error) {
      console.warn('No se pudo guardar el tema en localStorage:', error);
    }
  }, [isDarkMode]);

  // ---- Zoom automático ----
  // Se recalcula al cambiar tamaño/foco para soportar mejor múltiples monitores.
  const applyAutoZoom = useCallback(() => {
    const zoom = getAutoZoom();
    document.documentElement.style.zoom = String(zoom);
    document.documentElement.style.setProperty('--app-zoom', String(zoom));
  }, []);

  useEffect(() => {
    applyAutoZoom();
    window.addEventListener('resize', applyAutoZoom);
    window.addEventListener('focus', applyAutoZoom);
    document.addEventListener('visibilitychange', applyAutoZoom);

    return () => {
      window.removeEventListener('resize', applyAutoZoom);
      window.removeEventListener('focus', applyAutoZoom);
      document.removeEventListener('visibilitychange', applyAutoZoom);
    };
  }, [applyAutoZoom]);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => !prevMode);
  };

  const setLightTheme = () => setIsDarkMode(false);
  const setDarkTheme = () => setIsDarkMode(true);

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const themeContextValue = {
    isDarkMode,
    isLightMode: !isDarkMode,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    theme: currentTheme,
    themeName: isDarkMode ? 'dark' : 'light'
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <StyledThemeProvider theme={{ ...currentTheme, isDark: isDarkMode }}>
        <GlobalStyles />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
